/**
 * Chat Store - Gestisce le chat AI tramite GraphQL
 *
 * Tutte le operazioni passano dal backend GraphQL.
 * Zustand gestisce solo lo stato UI (streaming, status).
 */

import { create } from "zustand";
import { graphqlQuery, graphqlMutation } from '../database/graphqlClient';

// =============================================================================
// TYPES
// =============================================================================

export type ChatMessageRole = "user" | "assistant";

export interface ChatAttachment {
  id: string;
  name: string;
  type: string; // MIME type
  size: number;
  url: string; // Full URL to access the file
}

/**
 * Status of a tool call in the message
 */
export type ToolCallStatus = 'pending' | 'executed' | 'failed' | 'awaiting_confirmation' | 'cancelled';

/**
 * Tool call information attached to a chat message
 */
export interface MessageToolCall {
  /** Tool name */
  tool: string;
  /** Parameters used */
  parameters: Record<string, unknown>;
  /** Current status */
  status: ToolCallStatus;
  /** Result data if executed */
  result?: unknown;
  /** Error message if failed */
  error?: string;
  /** Timestamp of execution */
  executedAt?: string;
}

/**
 * A pending confirmation for destructive operations
 */
export interface PendingConfirmation {
  /** Name of the tool awaiting confirmation */
  tool: string;
  /** Parameters that will be used */
  parameters: Record<string, unknown>;
  /** Human-readable description of what will be done */
  description: string;
  /** Entity type being affected */
  entityType?: string;
  /** Entity ID being affected */
  entityId?: string;
  /** Entity name for display */
  entityName?: string;
}

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  content: string;
  timestamp: string;
  transcribed?: boolean;
  attachments?: ChatAttachment[];
  searchQuery?: string;
  searchResultsId?: string;
  /** Tool call information if this message triggered a tool */
  toolCall?: MessageToolCall;
}

export interface ChatFolder {
  id: string;
  name: string;
  color?: string;
  createdAt: string;
  order: number;
  archived?: boolean;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  folderId?: string;
  archived?: boolean;
  favorite?: boolean;
}

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const AI_SESSION_FIELDS = `
  id
  title
  folderId
  archived
  favorite
  createdAt
  updatedAt
`;

const AI_MESSAGE_FIELDS = `
  id
  role
  content
  timestamp
  transcribed
  attachments
  searchResultsId
`;

const FOLDER_FIELDS = `
  id
  name
  color
  order
  archived
  createdAt
`;

const QUERIES = {
  sessions: `query { aiChatSessions { ${AI_SESSION_FIELDS} } }`,
  sessionWithMessages: `query GetSession($id: ID!) {
    aiChatSession(id: $id) {
      ${AI_SESSION_FIELDS}
      messages { ${AI_MESSAGE_FIELDS} }
    }
  }`,
  sessionMessages: `query GetSessionMessages($sessionId: String!) {
    aiChatMessages(sessionId: $sessionId) { ${AI_MESSAGE_FIELDS} }
  }`,
  folders: `query { aiChatFolders { ${FOLDER_FIELDS} } }`,
};

const MUTATIONS = {
  createSession: `mutation CreateAiChatSession($input: AiChatSessionInput!) {
    createAiChatSession(input: $input) { ${AI_SESSION_FIELDS} }
  }`,
  updateSession: `mutation UpdateAiChatSession($id: ID!, $input: AiChatSessionInput!) {
    updateAiChatSession(id: $id, input: $input) { ${AI_SESSION_FIELDS} }
  }`,
  deleteSession: `mutation DeleteAiChatSession($id: ID!) {
    deleteAiChatSession(id: $id)
  }`,
  addMessage: `mutation AddAiChatMessage($sessionId: ID!, $input: AiChatMessageInput!) {
    addAiChatMessage(sessionId: $sessionId, input: $input) { ${AI_MESSAGE_FIELDS} }
  }`,
  createFolder: `mutation CreateAiChatFolder($input: AiChatFolderInput!) {
    createAiChatFolder(input: $input) { ${FOLDER_FIELDS} }
  }`,
  updateFolder: `mutation UpdateAiChatFolder($id: ID!, $input: AiChatFolderInput!) {
    updateAiChatFolder(id: $id, input: $input) { ${FOLDER_FIELDS} }
  }`,
  deleteFolder: `mutation DeleteAiChatFolder($id: ID!) {
    deleteAiChatFolder(id: $id)
  }`,
};

// =============================================================================
// STORE STATE
// =============================================================================

interface ChatState {
  // Data from GraphQL
  sessions: ChatSession[];
  folders: ChatFolder[];

  // UI State (local only)
  activeSessionId: string | null;
  streamingText: string;
  status: "idle" | "loading" | "streaming" | "error" | "executing_tool";
  error: string | null;
  initialized: boolean;

  // Tool calling state
  pendingConfirmation: PendingConfirmation | null;
  toolCallCount: number; // Track tool calls per message to prevent infinite loops

  // Data Actions (GraphQL)
  loadData: () => Promise<void>;
  createSession: (firstMessage?: string) => Promise<string>;
  setActiveSession: (id: string | null) => Promise<void>;
  addMessage: (sessionId: string, message: Omit<ChatMessage, "id" | "timestamp">) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  archiveSession: (sessionId: string) => Promise<void>;
  unarchiveSession: (sessionId: string) => Promise<void>;
  duplicateSession: (sessionId: string) => Promise<string>;
  moveSessionToFolder: (sessionId: string, folderId: string | null) => Promise<void>;
  toggleSessionFavorite: (sessionId: string) => Promise<void>;

  // Folder Actions
  createFolder: (name: string, color?: string) => Promise<string>;
  renameFolder: (folderId: string, name: string) => Promise<void>;
  changeFolderColor: (folderId: string, color: string) => Promise<void>;
  archiveFolder: (folderId: string) => Promise<void>;
  unarchiveFolder: (folderId: string) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  reorderFolders: (folderIds: string[]) => Promise<void>;

  // UI Actions (local only)
  setStreamingText: (text: string) => void;
  setStatus: (status: ChatState["status"]) => void;
  setError: (error: string | null) => void;

  // Tool calling actions
  setPendingConfirmation: (confirmation: PendingConfirmation | null) => void;
  incrementToolCallCount: () => void;
  resetToolCallCount: () => void;
  updateMessageToolCall: (sessionId: string, messageId: string, toolCall: Partial<MessageToolCall>) => void;

  // Getters
  getActiveSession: () => ChatSession | null;
  getSessionMessages: (sessionId: string) => ChatMessage[];
  getSessionsByFolder: (folderId: string | null) => ChatSession[];
  getArchivedSessions: () => ChatSession[];
  getActiveFolders: () => ChatFolder[];
  getArchivedFolders: () => ChatFolder[];
  clearSessions: () => Promise<void>;
}

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const useChatStore = create<ChatState>()((set, get) => ({
  // Initial state
  sessions: [],
  folders: [],
  activeSessionId: null,
  streamingText: "",
  status: "idle",
  error: null,
  initialized: false,
  pendingConfirmation: null,
  toolCallCount: 0,

  // Load all data from GraphQL
  loadData: async () => {
    console.log('[ChatStore] loadData() called');
    try {
      set({ status: "loading" });

      console.log('[ChatStore] Fetching sessions and folders from GraphQL...');
      const [sessionsResult, foldersResult] = await Promise.all([
        graphqlQuery<{ aiChatSessions: Omit<ChatSession, 'messages'>[] }>(QUERIES.sessions).catch((e) => {
          console.error('[ChatStore] Failed to load sessions:', e);
          return { aiChatSessions: [] };
        }),
        graphqlQuery<{ aiChatFolders: ChatFolder[] }>(QUERIES.folders).catch((e) => {
          console.error('[ChatStore] Failed to load folders:', e);
          return { aiChatFolders: [] };
        }),
      ]);

      console.log('[ChatStore] GraphQL response:', {
        sessions: sessionsResult.aiChatSessions?.length ?? 0,
        folders: foldersResult.aiChatFolders?.length ?? 0
      });

      // Sessions from GraphQL don't include messages - add empty array
      const sessionsWithMessages: ChatSession[] = (sessionsResult.aiChatSessions || []).map(s => ({
        ...s,
        messages: [], // Messages are loaded on-demand when session is opened
      }));

      console.log('[ChatStore] Loaded', sessionsWithMessages.length, 'sessions');

      set({
        sessions: sessionsWithMessages,
        folders: foldersResult.aiChatFolders || [],
        initialized: true,
        status: "idle",
      });
    } catch (error) {
      console.error('[ChatStore] Failed to load chat data:', error);
      set({ initialized: true, status: "error", error: String(error) });
    }
  },

  createSession: async (firstMessage?: string) => {
    const title = firstMessage
      ? firstMessage.slice(0, 40) + (firstMessage.length > 40 ? "..." : "")
      : `Chat ${new Date().toLocaleString("it-IT")}`;

    try {
      const result = await graphqlMutation<{ createAiChatSession: ChatSession }>(
        MUTATIONS.createSession,
        { input: { title } }
      );

      // Check if GraphQL returned valid data
      if (!result.createAiChatSession?.id) {
        throw new Error('GraphQL returned no session data');
      }

      const newSession = {
        ...result.createAiChatSession,
        messages: [],
      };

      set((state) => ({
        sessions: [newSession, ...state.sessions],
        activeSessionId: newSession.id,
      }));

      return newSession.id;
    } catch (error) {
      // Silently use fallback for schema errors (backend types not yet implemented)
      const errorMsg = error instanceof Error ? error.message : '';
      if (!errorMsg.includes('Unknown type')) {
        console.warn('[ChatStore] Server unavailable, using local session:', errorMsg);
      }
      // Fallback: create local-only session
      const id = `local-${Date.now()}`;
      const now = new Date().toISOString();
      const session: ChatSession = {
        id,
        title,
        messages: [],
        createdAt: now,
        updatedAt: now,
      };
      set((state) => ({
        sessions: [session, ...state.sessions],
        activeSessionId: id,
      }));
      return id;
    }
  },

  setActiveSession: async (id) => {
    set({ activeSessionId: id });

    // Load messages for this session if not already loaded
    if (id) {
      const state = get();
      const session = state.sessions.find(s => s.id === id);

      // Skip if messages already loaded or local-only session
      if (session && session.messages.length === 0 && !id.startsWith('local-')) {
        try {
          const result = await graphqlQuery<{ aiChatMessages: ChatMessage[] }>(
            QUERIES.sessionMessages,
            { sessionId: id }
          );

          if (result.aiChatMessages && result.aiChatMessages.length > 0) {
            set((state) => ({
              sessions: state.sessions.map(s =>
                s.id === id
                  ? { ...s, messages: result.aiChatMessages }
                  : s
              ),
            }));
          }
        } catch (error) {
          console.error('[ChatStore] Failed to load messages for session:', id, error);
        }
      }
    }
  },

  addMessage: async (sessionId, message) => {
    const now = new Date().toISOString();
    const tempId = `msg-${Date.now()}`;

    console.log('[ChatStore] addMessage called:', {
      sessionId,
      role: message.role,
      contentLength: message.content.length,
    });

    const fullMessage: ChatMessage = {
      ...message,
      id: tempId,
      timestamp: now,
    };

    // Check if session exists
    const currentState = get();
    const sessionExists = currentState.sessions.some(s => s.id === sessionId);
    console.log('[ChatStore] Session exists:', sessionExists, 'Total sessions:', currentState.sessions.length);

    if (!sessionExists) {
      console.error('[ChatStore] SESSION NOT FOUND:', sessionId);
      console.log('[ChatStore] Available sessions:', currentState.sessions.map(s => ({ id: s.id, title: s.title })));
      return; // Don't proceed if session doesn't exist
    }

    // Optimistic update
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId
          ? {
              ...s,
              messages: [...s.messages, fullMessage],
              updatedAt: now,
              title: s.messages.length === 0 && message.role === "user"
                ? message.content.slice(0, 40) + (message.content.length > 40 ? "..." : "")
                : s.title,
            }
          : s
      ),
    }));

    // Verify optimistic update worked
    const afterUpdate = get();
    const updatedSession = afterUpdate.sessions.find(s => s.id === sessionId);
    console.log('[ChatStore] After optimistic update:', {
      messagesCount: updatedSession?.messages.length,
      lastMsgId: updatedSession?.messages[updatedSession.messages.length - 1]?.id,
    });

    try {
      const result = await graphqlMutation<{ addAiChatMessage: ChatMessage }>(
        MUTATIONS.addMessage,
        {
          sessionId,
          input: {
            role: message.role,
            content: message.content,
            transcribed: message.transcribed,
            attachments: message.attachments ? JSON.stringify(message.attachments) : null,
            searchResultsId: message.searchResultsId,
          },
        }
      );

      // Update with real ID from server
      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                messages: s.messages.map((m) =>
                  m.id === tempId ? { ...m, id: result.addAiChatMessage.id } : m
                ),
              }
            : s
        ),
      }));
    } catch (error) {
      // Silently ignore schema errors (backend types not yet implemented)
      // The local message is kept, so functionality is preserved
      const errorMsg = error instanceof Error ? error.message : '';
      if (!errorMsg.includes('Unknown type')) {
        console.warn('[ChatStore] Failed to persist message (offline mode):', errorMsg);
      }
    }
  },

  updateSessionTitle: async (sessionId, title) => {
    // Optimistic update
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, title, updatedAt: new Date().toISOString() } : s
      ),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateSession, {
        id: sessionId,
        input: { title },
      });
    } catch (error) {
      console.error('Failed to update session title:', error);
    }
  },

  deleteSession: async (sessionId) => {
    const state = get();

    // Optimistic update
    const newSessions = state.sessions.filter((s) => s.id !== sessionId);
    set({
      sessions: newSessions,
      activeSessionId: state.activeSessionId === sessionId
        ? (newSessions[0]?.id || null)
        : state.activeSessionId,
    });

    try {
      await graphqlMutation(MUTATIONS.deleteSession, { id: sessionId });
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  },

  archiveSession: async (sessionId) => {
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, archived: true, updatedAt: new Date().toISOString() } : s
      ),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateSession, {
        id: sessionId,
        input: { archived: true },
      });
    } catch (error) {
      console.error('Failed to archive session:', error);
    }
  },

  unarchiveSession: async (sessionId) => {
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, archived: false, updatedAt: new Date().toISOString() } : s
      ),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateSession, {
        id: sessionId,
        input: { archived: false },
      });
    } catch (error) {
      console.error('Failed to unarchive session:', error);
    }
  },

  duplicateSession: async (sessionId) => {
    const state = get();
    const session = state.sessions.find((s) => s.id === sessionId);
    if (!session) return '';

    try {
      const result = await graphqlMutation<{ createAiChatSession: ChatSession }>(
        MUTATIONS.createSession,
        { input: { title: `${session.title} (copia)` } }
      );

      const newSession = {
        ...result.createAiChatSession,
        messages: [],
      };

      // Copy messages
      for (const msg of session.messages) {
        await graphqlMutation(MUTATIONS.addMessage, {
          sessionId: newSession.id,
          input: {
            role: msg.role,
            content: msg.content,
            transcribed: msg.transcribed,
            attachments: msg.attachments ? JSON.stringify(msg.attachments) : null,
            searchResultsId: msg.searchResultsId,
          },
        });
      }

      set((state) => ({
        sessions: [{ ...newSession, messages: session.messages }, ...state.sessions],
        activeSessionId: newSession.id,
      }));

      return newSession.id;
    } catch (error) {
      console.error('Failed to duplicate session:', error);
      return '';
    }
  },

  moveSessionToFolder: async (sessionId, folderId) => {
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, folderId: folderId || undefined, updatedAt: new Date().toISOString() } : s
      ),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateSession, {
        id: sessionId,
        input: { folderId },
      });
    } catch (error) {
      console.error('Failed to move session:', error);
    }
  },

  toggleSessionFavorite: async (sessionId) => {
    const state = get();
    const session = state.sessions.find((s) => s.id === sessionId);
    if (!session) return;

    const newFavorite = !session.favorite;

    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, favorite: newFavorite, updatedAt: new Date().toISOString() } : s
      ),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateSession, {
        id: sessionId,
        input: { favorite: newFavorite },
      });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  },

  // Folder actions
  createFolder: async (name, color) => {
    try {
      const result = await graphqlMutation<{ createAiChatFolder: ChatFolder }>(
        MUTATIONS.createFolder,
        { input: { name, color, order: get().folders.length } }
      );

      set((state) => ({
        folders: [...state.folders, result.createAiChatFolder],
      }));

      return result.createAiChatFolder.id;
    } catch (error) {
      console.error('Failed to create folder:', error);
      return '';
    }
  },

  renameFolder: async (folderId, name) => {
    set((state) => ({
      folders: state.folders.map((f) => (f.id === folderId ? { ...f, name } : f)),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateFolder, {
        id: folderId,
        input: { name },
      });
    } catch (error) {
      console.error('Failed to rename folder:', error);
    }
  },

  changeFolderColor: async (folderId, color) => {
    set((state) => ({
      folders: state.folders.map((f) => (f.id === folderId ? { ...f, color } : f)),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateFolder, {
        id: folderId,
        input: { color },
      });
    } catch (error) {
      console.error('Failed to change folder color:', error);
    }
  },

  archiveFolder: async (folderId) => {
    set((state) => ({
      folders: state.folders.map((f) => (f.id === folderId ? { ...f, archived: true } : f)),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateFolder, {
        id: folderId,
        input: { archived: true },
      });
    } catch (error) {
      console.error('Failed to archive folder:', error);
    }
  },

  unarchiveFolder: async (folderId) => {
    set((state) => ({
      folders: state.folders.map((f) => (f.id === folderId ? { ...f, archived: false } : f)),
    }));

    try {
      await graphqlMutation(MUTATIONS.updateFolder, {
        id: folderId,
        input: { archived: false },
      });
    } catch (error) {
      console.error('Failed to unarchive folder:', error);
    }
  },

  deleteFolder: async (folderId) => {
    // Move sessions out of folder first
    set((state) => ({
      folders: state.folders.filter((f) => f.id !== folderId),
      sessions: state.sessions.map((s) =>
        s.folderId === folderId ? { ...s, folderId: undefined } : s
      ),
    }));

    try {
      await graphqlMutation(MUTATIONS.deleteFolder, { id: folderId });
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  },

  reorderFolders: async (folderIds) => {
    const state = get();
    const reorderedFolders = folderIds
      .map((id, index) => {
        const folder = state.folders.find((f) => f.id === id);
        return folder ? { ...folder, order: index } : null;
      })
      .filter(Boolean) as ChatFolder[];

    set({ folders: reorderedFolders });

    // Update each folder on server
    try {
      await Promise.all(
        reorderedFolders.map((folder) =>
          graphqlMutation(MUTATIONS.updateFolder, {
            id: folder.id,
            input: { order: folder.order },
          })
        )
      );
    } catch (error) {
      console.error('Failed to reorder folders:', error);
    }
  },

  // UI Actions
  setStreamingText: (text) => {
    set({ streamingText: text });
  },

  setStatus: (status) => {
    set({ status });
  },

  setError: (error) => {
    set({ error, status: error ? "error" : "idle" });
  },

  // Tool calling actions
  setPendingConfirmation: (confirmation) => {
    set({ pendingConfirmation: confirmation });
  },

  incrementToolCallCount: () => {
    set((state) => ({ toolCallCount: state.toolCallCount + 1 }));
  },

  resetToolCallCount: () => {
    set({ toolCallCount: 0 });
  },

  updateMessageToolCall: (sessionId, messageId, toolCallUpdate) => {
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId
          ? {
              ...s,
              messages: s.messages.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      toolCall: m.toolCall
                        ? { ...m.toolCall, ...toolCallUpdate }
                        : (toolCallUpdate as MessageToolCall),
                    }
                  : m
              ),
            }
          : s
      ),
    }));
  },

  // Getters
  getActiveSession: () => {
    const state = get();
    return state.sessions.find((s) => s.id === state.activeSessionId) || null;
  },

  getSessionMessages: (sessionId) => {
    const state = get();
    const session = state.sessions.find((s) => s.id === sessionId);
    return session?.messages || [];
  },

  getSessionsByFolder: (folderId) => {
    const state = get();
    return state.sessions.filter((s) =>
      !s.archived && (folderId === null ? !s.folderId : s.folderId === folderId)
    );
  },

  getArchivedSessions: () => {
    const state = get();
    return state.sessions.filter((s) => s.archived);
  },

  getActiveFolders: () => {
    const state = get();
    return state.folders.filter((f) => !f.archived).sort((a, b) => a.order - b.order);
  },

  getArchivedFolders: () => {
    const state = get();
    return state.folders.filter((f) => f.archived).sort((a, b) => a.order - b.order);
  },

  clearSessions: async () => {
    const state = get();

    try {
      await Promise.all(state.sessions.map((s) =>
        graphqlMutation(MUTATIONS.deleteSession, { id: s.id })
      ));
    } catch (error) {
      console.error('Failed to clear sessions:', error);
    }

    set({ sessions: [], activeSessionId: null });
  },
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectRecentSessions = (state: ChatState) =>
  state.sessions.filter(s => !s.archived).slice(0, 10);

export const selectActiveMessages = (state: ChatState) => {
  const session = state.sessions.find((s) => s.id === state.activeSessionId);
  return session?.messages || [];
};
